import classNames from 'classnames';
import { Button, Modal } from 'common/components';
import { isString } from 'lodash';
import React, { useState } from 'react';
import type { Data as RowData } from '../../../CtResultTable';
import type { IdCard as IIdCard, Module } from '../../Expand';
import * as Info from './info';
import './IdCard.scss';

export type Ocr = {
  expiryDate: string;
  expiryDate2: string;
  driverNo: string;
  idType: string;
  issueDate: string;
  juminNo1: string;
  juminNo2: string;
  _juminNo2: string;
  issueNo1: string;
  issueNo2: string;
  _issueNo2: string;
  transaction_id: string;
  passportNo: string;
  userName: string;
  nationality: string;
  birthDate: string;
};

interface Props {
  content: IIdCard;
  rowData: RowData;
  module: Module;
  isExpired: boolean;
}

export const dateFormatter = (date: string): string => {
  if (date.length !== 8) {
    return date;
  }
  return `${date.slice(0, 4)}.${date.slice(4, 6)}.${date.slice(6, 8)}`;
};

const IdCard: React.FC<Props> = ({ content, rowData, module, isExpired }) => {
  const [mask, setMask] = useState(true);
  const { is_uploaded, is_manual_input, original_ocr_data, modified_ocr_data, verified, id_card_image, id_card_origin, uploaded_type } = content; // prettier-ignore
  let origin: Ocr = {
    expiryDate: '',
    expiryDate2: '',
    driverNo: '',
    idType: '9',
    issueDate: '',
    juminNo1: '',
    juminNo2: '',
    _juminNo2: '',
    issueNo1: '',
    issueNo2: '',
    _issueNo2: '',
    transaction_id: '',
    passportNo: '',
    userName: '',
    nationality: '',
    birthDate: '',
  };
  let modified: Ocr | null = null;
  if (!isExpired) {
    origin = JSON.parse(`${original_ocr_data}`.replaceAll('\\', ''));
    modified = JSON.parse(`${modified_ocr_data}`.replaceAll('\\', ''));
  }
  const idType = modified ? modified.idType : origin.idType;

  const stringTypes = (idType: string): string => {
    // prettier-ignore
    switch (String(idType)) {
      case '1': return '주민등록증';
      case '2': return '운전면허증';
      case '3': return '한국 여권';
      case '4': return '외국 여권';
      case '5' : return '외국인등록증(국내거소증/영주증)';
      case '5-1': return '외국인등록증';
      case '5-2': return '거소신고증';
      case '5-3': return '영주증';
      case '9': return '만료됨';
      default: return '미등록';
    }
  };

  // ['주민등록증', '주민등록번호']
  // ['운전면허증', '주민등록번호']
  // ['한국 여권', '주민등록번호']
  // ['외국 여권', '주민등록번호']
  // ['외국인등록증', '외국인등록번호']
  // ['거소신고증', '거소신고번호']
  // ['영주증', '외국인등록번호']
  // ['미등록', '미등록']

  // 변경한 데이터인지 확인
  const isModified = (keys: (keyof Ocr)[] | keyof Ocr): boolean => {
    if (!modified) return false;
    if (isString(keys)) return origin[keys] !== modified[keys];
    return !!keys.some((k) => modified && origin[k] !== modified[k]);
  };

  const uploadType = (uploaded_type: string) => {
    if (uploaded_type === 'mobile') return '모바일';
    if (uploaded_type === 'pc') return uploaded_type.toUpperCase();
    if (uploaded_type === 'camera') return '카메라';
    return uploaded_type;
  };

  const showOriginalImage = () => {
    if (is_manual_input) return false; // 직접 입력은 원본이미지 제공X
    if (!id_card_origin) return false; // 신분증 OCR 원본이미지가 없으면 제공X
    if (idType === '4') return false; // 해외 여권은 뒷번호가 없기 때문에 제공X
    if (idType === '3' && modified !== null && !modified.juminNo2) return false; // 국내 여권 중 주민번호 뒷자리가 없는 신여권은 제공X
    return true;
  };

  const props = { origin, modified, rowData, isModified };
  return (
    <div id="reviewer-review-table-expand-content-idcard">
      <ul className="info-section">
        <li>
          <h4>신분증 정보</h4>
          {idType === '5' ? (
            <div>
              <p>외국인등록증</p>
              <p>(국내거소증/영주증)</p>
            </div>
          ) : (
            <p>{stringTypes(modified ? modified.idType : origin.idType)}</p>
          )}
        </li>
        <li>
          <h4>사진 첨부 여부</h4>
          <p className={classNames({ warning: is_uploaded })}>
            {is_uploaded ? 'YES' : 'NO'}
            {is_uploaded && uploaded_type ? <span className="upload-type">{uploadType(uploaded_type)}</span> : <></>}
          </p>
        </li>
        <li>
          <h4>직접 입력 여부</h4>
          <p className={classNames({ warning: is_manual_input })}> {is_manual_input ? 'YES' : 'NO'}</p>
        </li>
        <li>
          <h4>정보 수정 여부</h4>
          <p className={classNames({ warning: modified })}>{modified ? 'YES' : 'NO'}</p>
        </li>

        {isExpired ? (
          <li className="id-info">
            <p className="id-expired">저장 기간 만료로 인해 개인 정보가 삭제되었습니다.</p>
          </li>
        ) : (
          <li className="id-info">
            {idType === '1' && <Info.ID {...props} />}
            {idType === '2' && <Info.DriverLicense {...props} />}
            {idType === '3' && <Info.PassPort {...props} />}
            {idType === '4' && <Info.Foreign.PassPort {...props} />}
            {['5', '5-1', '5-2', '5-3'].includes(idType) && <Info.Foreign.ID {...props} idType={idType} />}
          </li>
        )}

        <li>
          <h4>신분증 진위확인</h4>
          {(!module.id_card_verification && <p className={classNames({ warning: !verified })}>미사용</p>) || (
            <p className={classNames({ warning: !verified })}>{verified ? '확인' : '실패'}</p>
          )}
        </li>
      </ul>

      <section className="image-section">
        {isExpired ? (
          <div className="image-expired">
            <i />
          </div>
        ) : (
          <>
            <Modal
              content={({ closeHandler }) => {
                return (
                  <div className="reviewer-image-section-modal">
                    <i className="close-btn" onClick={closeHandler} />
                    <img
                      style={{ width: '100%' }}
                      src={`data:image/jpeg;base64,${mask ? id_card_image : id_card_origin}`}
                      alt="id-card"
                    />
                  </div>
                );
              }}
            >
              <div className="image-field">
                <img src={`data:image/jpeg;base64,${mask ? id_card_image : id_card_origin}`} alt="id-card" />
                <i />
              </div>
            </Modal>

            {showOriginalImage() && (
              <Button text={mask ? '원본 사진 보기' : '마스킹 사진 보기'} onClick={() => setMask(!mask)} />
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default IdCard;
